/**
 * Capture Chrome/Edge "Add to Home Screen" / install prompt early.
 * Browsers only fire beforeinstallprompt once; we must stash it until the patient taps Allow.
 */

let deferredPrompt = null;
const listeners = new Set();

function notify() {
    listeners.forEach((fn) => {
        try {
            fn(deferredPrompt);
        } catch (_e) {
            /* ignore */
        }
    });
}

export function initPatientInstallPromptCapture() {
    if (typeof window === 'undefined' || window.__liveassistInstallCapture) {
        return;
    }
    window.__liveassistInstallCapture = true;

    window.addEventListener('beforeinstallprompt', (event) => {
        try {
            event.preventDefault();
        } catch (_e) {
            /* ignore */
        }
        deferredPrompt = event;
        notify();
    });

    window.addEventListener('appinstalled', () => {
        deferredPrompt = null;
        try {
            // Only meaningful while the app is actually installed.
            // If patient later uninstalls, patientRoomAccess clears this when not in standalone mode.
            localStorage.setItem('liveassist_a2hs_installed', '1');
        } catch (_e) {
            /* ignore */
        }
        notify();
    });
}

export function getPatientInstallPrompt() {
    return deferredPrompt;
}

export function hasPatientInstallPrompt() {
    return !!deferredPrompt;
}

export function onPatientInstallPromptChange(callback) {
    if (typeof callback !== 'function') {
        return () => { };
    }
    listeners.add(callback);
    try {
        callback(deferredPrompt);
    } catch (_e) {
        /* ignore */
    }
    return () => {
        listeners.delete(callback);
    };
}

/**
 * Shows the browser install sheet. Patient taps Install / Add → shortcut is created.
 * @returns {{ outcome: 'accepted' | 'dismissed' | 'unavailable' }}
 */
export async function promptPatientInstall() {
    if (!deferredPrompt || typeof deferredPrompt.prompt !== 'function') {
        return { outcome: 'unavailable' };
    }

    const event = deferredPrompt;
    deferredPrompt = null;
    notify();

    try {
        await event.prompt();
        const choice = await event.userChoice;
        const outcome = choice?.outcome === 'accepted' ? 'accepted' : 'dismissed';
        return { outcome };
    } catch (_e) {
        return { outcome: 'unavailable' };
    }
}
